<template>
  <MainLayout class="!gap-30">
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="blog">
      <div class="flex flex-col items-center gap-y-8 lg:mt-[2rem] lg:mb-[1rem]">
        <span
          class="inline-flex items-center gap-x-2 text-sm font-semibold px-4 py-1.5 rounded-full border border-primary/25 bg-white drop-shadow-sm text-lighter">
          <span
            class="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3696] to-[#FF7C53] inline-block"></span>
          Nieuws &amp; inspiratie
        </span>

        <h1
          class="text-[38px] leading-[42px] lg:text-[76px] lg:leading-[80px] font-bold text-dark text-center font-markPro tracking-[-0.1rem] max-w-[18ch]">
          Het laatste van<br />
          <span class="text-gradient-primary whitespace-nowrap">Bistroo</span>
        </h1>

        <p class="text-center text-lighter lg:text-lg font-semibold max-w-[52ch] leading-relaxed">
          Verhalen, updates en tips van het Bistroo team, voor horecaondernemers die online meer
          willen halen.
        </p>

        <div class="flex flex-wrap justify-center gap-2 mt-2">
          <button
            class="px-4 py-1.5 rounded-full text-sm font-semibold border transition-all duration-200"
            :class="
              activeCategoryId === null
                ? 'bg-gradient-to-r from-[#FF3696] to-[#FF7C53] text-white border-transparent'
                : 'bg-white text-lighter border-[#ECEDF0] hover:border-primary/40 drop-shadow-sm'
            "
            @click="selectCategory(null)">
            Alles
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="px-4 py-1.5 rounded-full text-sm font-semibold border transition-all duration-200"
            :class="
              activeCategoryId === cat.id
                ? 'bg-gradient-to-r from-[#FF3696] to-[#FF7C53] text-white border-transparent'
                : 'bg-white text-lighter border-[#ECEDF0] hover:border-primary/40 drop-shadow-sm'
            "
            @click="selectCategory(cat.id)">
            {{ cat.name }}
          </button>
        </div>
      </div>

      <div v-if="request.loading.value" class="flex justify-center items-center py-24">
        <div class="flex flex-col items-center gap-y-4">
          <div
            class="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
          <p class="text-lighter font-semibold">Artikelen laden...</p>
        </div>
      </div>

      <div v-else-if="error" class="flex justify-center items-center py-24">
        <div class="bg-white rounded-xl drop-shadow p-8 text-center max-w-[400px]">
          <i class="bi bi-exclamation-circle text-4xl text-lighter mb-4 block"></i>
          <p class="text-dark font-semibold">Er ging iets mis bij het laden van de artikelen.</p>
          <p class="text-lighter mt-2">Probeer de pagina te vernieuwen.</p>
        </div>
      </div>

      <div
        v-else-if="request.items.value.length < 1"
        class="flex justify-center items-center py-24">
        <div class="bg-white rounded-xl drop-shadow p-8 text-center max-w-[400px]">
          <i class="bi bi-journal-text text-4xl text-lighter mb-4 block"></i>
          <p class="text-dark font-semibold">Nog geen artikelen beschikbaar.</p>
        </div>
      </div>

      <div v-else class="w-full">
        <div v-if="featuredArticle" class="mb-10">
          <RouterLink
            :to="{ name: 'blogs.show', params: { slug: featuredArticle.slug } }"
            class="group block bg-white rounded-2xl drop-shadow-lg overflow-hidden lg:flex lg:h-[420px] hover:drop-shadow-xl transition-all duration-300">
            <div class="lg:w-1/2 overflow-hidden flex-shrink-0">
              <img
                v-if="featuredArticle.image_url"
                :src="featuredArticle.image_url"
                :alt="featuredArticle.title"
                class="w-full h-64 lg:h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div
                v-else
                class="w-full h-64 lg:h-full bg-gradient-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                <i class="bi bi-journal-richtext text-6xl text-primary/50"></i>
              </div>
            </div>
            <div class="p-8 lg:p-12 flex flex-col justify-between lg:w-1/2">
              <div class="flex flex-col gap-y-4">
                <div class="flex items-center gap-x-3">
                  <span
                    v-if="featuredArticle.category"
                    class="text-xs font-semibold text-white bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-3 py-1 rounded-full">
                    {{ featuredArticle.category.name }}
                  </span>
                  <span class="text-lighter text-sm">
                    {{ formatDate(featuredArticle.published_at) }}
                  </span>
                </div>
                <h2
                  class="text-[24px] lg:text-[32px] leading-tight font-bold text-dark font-markPro tracking-[-0.05rem] group-hover:text-gradient-primary transition-all duration-300">
                  {{ featuredArticle.title }}
                </h2>
                <p class="text-lighter leading-relaxed line-clamp-3">
                  {{ featuredArticle.excerpt }}
                </p>
              </div>
              <div class="flex items-center gap-x-2 mt-6 text-primary font-semibold">
                Lees meer
                <i
                  class="bi bi-arrow-right mt-[2px] transition-transform duration-300 group-hover:translate-x-1"></i>
              </div>
            </div>
          </RouterLink>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <RouterLink
            v-for="article in gridArticles"
            :key="article.id"
            :to="{ name: 'blogs.show', params: { slug: article.slug } }"
            class="group bg-white rounded-xl drop-shadow overflow-hidden flex flex-col hover:drop-shadow-lg transition-all duration-300">
            <div class="overflow-hidden h-48 flex-shrink-0">
              <img
                v-if="article.image_url"
                :src="article.image_url"
                :alt="article.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div
                v-else
                class="w-full h-full bg-gradient-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                <i class="bi bi-journal-richtext text-4xl text-primary/50"></i>
              </div>
            </div>
            <div class="p-6 flex flex-col gap-y-3 flex-grow">
              <div class="flex items-center gap-x-3">
                <span
                  v-if="article.category"
                  class="text-xs font-semibold text-white bg-gradient-to-r from-[#FF3696] to-[#FF7C53] px-3 py-1 rounded-full">
                  {{ article.category.name }}
                </span>
                <span class="text-lighter text-sm">{{ formatDate(article.published_at) }}</span>
              </div>
              <h3
                class="text-lg font-bold text-dark font-markPro tracking-[-0.03rem] leading-snug group-hover:text-gradient-primary transition-all duration-300 line-clamp-2">
                {{ article.title }}
              </h3>
              <p class="text-lighter text-sm leading-relaxed line-clamp-3 flex-grow">
                {{ article.excerpt }}
              </p>
              <div class="flex items-center gap-x-2 mt-2 text-primary font-semibold text-sm">
                Lees meer
                <i
                  class="bi bi-arrow-right mt-[2px] transition-transform duration-300 group-hover:translate-x-1"></i>
              </div>
            </div>
          </RouterLink>
        </div>

        <div
          v-if="request.meta.value.last_page > 1"
          class="flex justify-center items-center gap-2 mt-12">
          <button
            class="w-10 h-10 flex items-center justify-center rounded-full border border-[#ECEDF0] bg-white drop-shadow-sm text-lighter transition-all duration-200 hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="request.page.value < 1"
            @click="request.previousPage">
            <i class="bi bi-chevron-left text-sm"></i>
          </button>

          <template v-for="p in visiblePages" :key="String(p) + '_' + visiblePages.indexOf(p)">
            <button
              v-if="typeof p === 'number'"
              class="w-10 h-10 flex items-center justify-center rounded-full border text-sm font-semibold transition-all duration-200"
              :class="
                request.page.value === p
                  ? 'bg-gradient-to-r from-[#FF3696] to-[#FF7C53] text-white border-transparent drop-shadow'
                  : 'bg-white text-lighter border-[#ECEDF0] hover:border-primary/40 drop-shadow-sm'
              "
              @click="request.setPage(p)">
              {{ p }}
            </button>
            <span
              v-else
              class="w-10 h-10 flex items-center justify-center text-lighter font-semibold select-none">
              &hellip;
            </span>
          </template>

          <button
            class="w-10 h-10 flex items-center justify-center rounded-full border border-[#ECEDF0] bg-white drop-shadow-sm text-lighter transition-all duration-200 hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="request.page.value >= request.meta.value?.last_page"
            @click="request.nextPage">
            <i class="bi bi-chevron-right text-sm"></i>
          </button>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />
            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div
          class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30 lg:min-w-[1136px]">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import { useIndexRequest, withFilters, withUrlSync } from '@/composables/useIndexRequest'
import { useBlogHttp, type BlogCategory } from '@/http/useBlogHttp'
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { computed, onServerPrefetch, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

useHead(
  computed(() => ({
    title: 'Blog | Bistroo',
    meta: [
      {
        name: 'description',
        content:
          'Lees het laatste nieuws, tips en updates van het Bistroo team over online bestellen en de horeca.',
      },
    ],
  }))
)

const { categories: loadCategories, index } = useBlogHttp()

const categories = ref<BlogCategory[]>([])
const request = withUrlSync(() =>
  withFilters(
    () =>
      useIndexRequest(index, {
        per_page: 9,
      }),
    {
      blog_category_id: null as number | null,
    }
  )
)

const activeCategoryId = request.filters.blog_category_id

const error = ref(false)

const fetchCategories = async () => {
  loadCategories()
    .then((res) => {
      categories.value = res.data
    })
    .catch((err) => {
      console.error(err)
      categories.value = []
    })
}

const selectCategory = (id: number | null) => {
  activeCategoryId.value = id
  request.setPage(1)
}

fetchCategories()

onServerPrefetch(async () => {
  await fetchCategories()

  await request.handleFetch()
})

const featuredArticle = computed(() =>
  // Only first item of first page
  request.page.value === 1 && request.items.value.length > 0 ? request.items.value[0] : null
)

const gridArticles = computed(() => {
  // If no featured article, show all articles
  if (!featuredArticle.value) {
    return request.items.value
  }

  // If featured article, list every article except featured
  return request.items.value.filter((i) => i.id !== featuredArticle.value?.id)
})

const visiblePages = computed((): (number | '...')[] => {
  const total = request.lastPage.value
  const current = request.page.value

  if (total <= 7) {
    // Do not truncate the page links
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  const middle = Array.from({ length: end - start + 1 }, (_, i) => start + i)

  // Truncate the pages at the center (E.g.: `1 ... 3 4 5 ... 20``)
  return [
    1,
    ...(current > 3 ? ['...' as const] : []),
    ...middle,
    ...(current < total - 2 ? ['...' as const] : []),
    total,
  ]
})

const formatDate = (dateString: string | null): string => {
  if (!dateString) {
    return ''
  }
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
}
</script>

<style scoped>
@reference "@/assets/css/main.css";

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
